import { TextField, Button, Icon, Box, Title, ClientLogin, getMfaHash, getRememberMfa } from 'vopay';
import * as Yup from 'yup';
import { useState, useEffect, JSX } from 'react';
import { Formik } from 'formik';
import styles from './Login.module.scss';
import ResetPassword from './ResetPasswordRequest/ResetPasswordRequest';
import TwoFactorAuthenticationContainer from './TwoFactorAuthentication/TwoFactorAuthenticationContainer';
import ChangePassword from './ChangePassword/ChangePassword';
import { useClientLogin } from '../../providers/ClientLogin.provider';
import { PasswordFields } from './ChangePassword/ChangePasswordForm';
import { LogoutDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import LogoutContextDialogueContext, { LogoutCtx } from './Dialogue/LogoutContext';
import LogoutDialogue from './Dialogue/LogoutDialogue';
import { useTranslation } from 'react-i18next';

const LoginForm = (): JSX.Element => {
  const { isSuspiciousLogin, isSubmitting, ErrorMessage, login, response, storeLogin, isLogout, setMessageLogout } =
    useClientLogin();
  const [shouldShowLoginPage, setShouldShowLoginPage] = useState(true);
  const [shouldResetPassword, setShouldResetPassword] = useState(false);
  const [shouldShow2FA, setShouldShow2FA] = useState(false);
  const [shouldShowChangePassowrd, setShouldShowChangePassowrd] = useState(false);
  const [shouldShowLoginError, setShouldShowLoginError] = useState(false);
  const [loginfailMessage, setLoginFailMessage] = useState('');
  const [isChangePassword, setIsChangePassword] = useState(false);
  const [loginRequest, setLoginRequest] = useState({
    Username: '',
    Password: '',
  });
  const [logutinfo, setIsLogout] = useState(true);
  const { t } = useTranslation('translations', { keyPrefix: 'Login' });

  const TwoFAEnabled = import.meta.env.VITE_2FA_Enabled === 'true';
  const clientLogin = (value: ClientLogin) => {
    setLoginRequest({
      Username: value.Username || '',
      Password: value.Password || '',
    });
    login({
      Username: value.Username || '',
      Password: value.Password || '',
    });
  };

  const handleResetPassword = (value: boolean) => {
    setShouldResetPassword(value);
  };

  const handleLoginPage = (value: boolean) => {
    setShouldShowLoginPage(value);
  };

  useEffect(() => {
    setIsLogout(isLogout);
  }, [isLogout]);

  useEffect(() => {
    localStorage.removeItem('Password');
  }, []);

  useEffect(() => {
    if (response.Success !== undefined && !logutinfo && !isSubmitting) {
      if (isChangePassword) {
        storeLogin();
      } else if (!response.Success) {
        setShouldShowLoginError(true);
      } else if (TwoFAEnabled === true && !hasRememberedMfa()) {
        setShouldShowLoginPage(false);
        setShouldShow2FA(true);
      } else if (response.PasswordResetRequired) {
        setShouldShowLoginPage(false);
        setShouldShowChangePassowrd(true);
      } else {
        storeLogin();
      }
    }
  }, [response, isSubmitting]);

  function hasRememberedMfa() {
    if (response.MFAEnabled === false) return false;

    return getRememberMfa(response.LoginUserName) && !!getMfaHash(response.LoginUserName);
  }

  useEffect(() => {
    setLoginFailMessage(ErrorMessage);
  }, [ErrorMessage]);
  const Continue2Fa = () => {
    if (response.PasswordResetRequired) {
      setShouldShow2FA(false);
      setShouldShowChangePassowrd(true);
    } else {
      storeLogin();
    }
  };
  const Cancel2Fa = () => {
    setShouldShow2FA(false);
    setShouldShowLoginPage(true);
  };
  const ContinueChangePassword = (values: PasswordFields) => {
    setIsChangePassword(true);
    localStorage.setItem('Password', values.Password);
    login({
      Username: loginRequest.Username,
      Password: values.Password,
    });
  };
  const Logout: LogoutCtx = {
    showLogoutAlert: logutinfo,
    isSuspiciousLogin,
  };
  const handlers = {
    handleLogoutDialogue: (handlerProps: DialogueHandlerProps<LogoutDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;
      const dialogueHandlers: LogoutDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setMessageLogout(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };

  return (
    <div className={styles.loginContainerInner}>
      <LogoutContextDialogueContext.Provider value={Logout}>
        {shouldResetPassword && (
          <ResetPassword handleResetPassword={handleResetPassword} handleLoginPage={handleLoginPage} />
        )}
        {shouldShow2FA && (
          <TwoFactorAuthenticationContainer
            handleContinue={Continue2Fa}
            handleCancel={Cancel2Fa}
            loginRequest={loginRequest}
            loginUserName={loginRequest.Username.toLowerCase()}
          />
        )}
        {shouldShowChangePassowrd && (
          <ChangePassword handleContinue={ContinueChangePassword} loginRequest={loginRequest} />
        )}
        {shouldShowLoginPage && (
          <Box flx="col" width="100%">
            <Box mb="sm" flx="col" ai="center" height="auto">
              <Title title={t('title')} bold size="xsm" />
            </Box>
            <Formik
              initialValues={{
                Username: '',
                Password: '',
              }}
              validationSchema={loginValidationSchema()}
              onSubmit={clientLogin}
            >
              {({ values, handleChange, handleBlur, handleSubmit, errors, touched }) => (
                <form onSubmit={handleSubmit}>
                  <Box mb="sm" flx="col" height="auto">
                    <TextField
                      key="Username"
                      name="Username"
                      placeholder={t('Form.usernamePlaceholder')}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.Username}
                      variant="border"
                      height="50px"
                      elementLeft={<Icon icon="user" size="md" color="medium-dark" />}
                      error={errors.Username && touched.Username ? errors.Username : null}
                    />
                  </Box>
                  <Box mb="md" flx="col" height="auto">
                    <TextField
                      key="Password"
                      name="Password"
                      placeholder={t('Form.passwordPlaceholder')}
                      preset="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.Password}
                      variant="border"
                      height="50px"
                      elementLeft={<Icon icon="lock" size="md" color="medium-dark" />}
                      error={errors.Password && touched.Password ? errors.Password : null}
                    />
                  </Box>

                  {shouldShowLoginError && (
                    <div className={styles.errorMSG} style={{ paddingBottom: 15, paddingTop: 0 }}>
                      {loginfailMessage}
                    </div>
                  )}
                  <Box jc="fe" flx="col" height="auto">
                    <Button
                      id="login-submit"
                      type="submit"
                      label={t('Form.submitButton')}
                      size="xlg"
                      textSize={1}
                      isSubmitting={isSubmitting}
                    />
                  </Box>
                </form>
              )}
            </Formik>
            <Box mt="sm" height="auto" jc="center" ai="center">
              <Button
                id="forgot-password"
                type="button"
                label={t('Form.forgotPasswordButton')}
                color="none"
                noShadow
                noPadding
                bold
                textSize={1}
                onClick={() => {
                  setShouldResetPassword(true);
                  setShouldShowLoginPage(false);
                }}
              />
            </Box>
          </Box>
        )}
        <LogoutDialogue handleDialogue={handlers.handleLogoutDialogue} />
      </LogoutContextDialogueContext.Provider>
    </div>
  );

  function loginValidationSchema() {
    return Yup.object().shape({
      Username: Yup.string().label(t('Form.usernameLabel')).required(),
      Password: Yup.string().label(t('Form.passwordLabel')).required(),
    });
  }
};

export default LoginForm;
